/**
 * Content editor utility functions.
 */

export function debounce<T extends(...args: unknown[]) => void>(
  func: T,
  wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function getElementPath(element: Element): string {
  const path: string[] = []
  let current: Element | null = element

  while (current && current !== document.body) {
    const parentEl: Element | null = current.parentElement
    if (parentEl) {
      const index = Array.from(parentEl.children).indexOf(current)
      path.unshift(`${current.tagName.toLowerCase()}[${index}]`)
    }
    current = parentEl
  }

  return path.join(' > ')
}

export function log(message: string, data?: unknown): void {
  console.log(`[Musk Editor] ${message}`, data || '')
}

/** camelCase 样式属性名 → CSS 属性名（如 fontSize → font-size） */
export function camelStylePropToCss(property: string): string {
  if (property.includes('-')) return property
  return property.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)
}

/** 读取元素内联 style 上的值（不含优先级标记） */
export function getInlineStyleValue(el: HTMLElement, property: string): string {
  return el.style.getPropertyValue(camelStylePropToCss(property)).trim()
}

/** 记录变更前的值：优先内联，否则取 computed */
export function getStyleValueForRecord(el: HTMLElement, property: string): string {
  const inline = getInlineStyleValue(el, property)
  if (inline) return inline
  return window.getComputedStyle(el).getPropertyValue(camelStylePropToCss(property)).trim()
}

/** 写入内联样式并带 !important；value 为空则移除该属性 */
export function setInlineStyleImportant(el: HTMLElement, property: string, value: string): void {
  const cssProp = camelStylePropToCss(property)
  if (!value) {
    el.style.removeProperty(cssProp)
    return
  }
  el.style.setProperty(cssProp, value, 'important')
}

/**
 * 判断事件的 composedPath 是否包含 MathLive 的元素（快速路径）。
 * 类名覆盖 math-field / math-virtual-keyboard / .ML__* / .MLK__*。
 */
export function isMathLiveEvent(e: Event): boolean {
  const path = typeof e.composedPath === 'function' ? e.composedPath() : []
  for (let i = 0; i < path.length; i++) {
    const node = path[i] as Element
    if (!node || node.nodeType !== 1) continue
    const tag = (node.tagName || '').toLowerCase()
    if (tag === 'math-virtual-keyboard' || tag === 'math-field') return true
    const cls = node.classList
    if (!cls) continue
    // MathLive 内部类名以 ML__ 或 MLK__ 开头（keyboard / keycap / tab / backdrop / plate 等）
    for (let j = 0; j < cls.length; j++) {
      const c = cls[j]
      if (c.indexOf('ML__') === 0 || c.indexOf('MLK__') === 0) return true
    }
  }
  return false
}

/**
 * pointerdown 兜底：
 *
 * MathLive 切换键盘 tab（"123" / "abc" / "αβγ"）时，布局行数/按键排布会变，
 * 整个键盘的高度随之变化。用户按下时 pointerdown 命中了 .MLK__plate 上某个按键，
 * 抬起时原位置已成为键盘外空白区域。按 W3C 规范，
 *   click.target = pointerdown.target 和 pointerup.target 的最深共同祖先
 * 此时共同祖先就是 body，click 的 composedPath 里完全看不到 MathLive 节点，
 * 编辑器会误判为"点击面板外"而关闭公式面板。
 *
 * pointerdown 阶段 DOM 尚未变化,target 是可靠的。在 pointerdown 时打标记、
 * 在紧随的 click 阶段一次性消费 —— 如果 composedPath 判定失败但最近一次
 * pointerdown 确实在 MathLive 上,则仍视作 MathLive click。
 *
 * 参考: mathlive/src/virtual-keyboard/virtual-keyboard.ts 的 set currentLayer / render()
 * 只切换 .is-visible 类并改写 keycap.innerHTML,但不同 layer 高度不同导致几何变化。
 */
const MATHLIVE_POINTERDOWN_WINDOW_MS = 500
let _lastMathLivePointerDownAt = 0

export function markMathLivePointerDown(): void {
  _lastMathLivePointerDownAt = Date.now()
}

/** 一次性消费 MathLive pointerdown 标记。返回 true 表示当前事件应被视作 MathLive 事件。 */
export function consumeMathLivePointerDownMark(): boolean {
  if (!_lastMathLivePointerDownAt) return false
  const within = Date.now() - _lastMathLivePointerDownAt < MATHLIVE_POINTERDOWN_WINDOW_MS
  _lastMathLivePointerDownAt = 0
  return within
}

/**
 * 提取事件 target 的诊断信息，用于调试面板异常关闭。
 */
export function describeEventTarget(e: Event): Record<string, unknown> {
  const target = e.target as Element | null
  const path = typeof e.composedPath === 'function' ? e.composedPath() : []
  const pathTags: string[] = []
  for (let i = 0; i < Math.min(path.length, 8); i++) {
    const node = path[i] as Element
    if (!node || node.nodeType !== 1) {
      pathTags.push(String((path[i] as any)?.constructor?.name || path[i]))
      continue
    }
    const tag = (node.tagName || '').toLowerCase()
    const cls = node.className && typeof node.className === 'string'
      ? `.${node.className.split(/\s+/).slice(0, 2).join('.')}`
      : ''
    pathTags.push(`${tag}${cls}`)
  }
  return {
    tag: target?.tagName,
    class: (target as HTMLElement)?.className || '',
    id: (target as HTMLElement)?.id || '',
    isConnected: target?.isConnected,
    path: pathTags.join(' > '),
  }
}
